import React, { useEffect } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { useQuery } from "@tanstack/react-query";

import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseTextField from "../../../components/ui/BaseTextField";
import {
  createBulkVendor,
  fetchCategories,
} from "../../../services/api/category";

import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { showToast } from "../../../store/toast/toastSlice";

const validationSchema = yup.object({
  categoryId: yup.string("").required("Category is required"),
  name: yup.string().trim().required("Category is required"),
});

export default function AddEditVendorModal({
  open,
  close,
  mode = "add",
  initialData = null,
  refetchVendors,
}) {
  const isEdit = mode === "edit";
  const dispatch = useDispatch();

  const {
    isPending: isCreateVendorLoading,
    error: createVendorError,
    mutateAsync: createVendorMutation,
  } = useMutation({ mutationFn: createBulkVendor });

  const handleCreateVendor = async () => {
    try {
      await createVendorMutation({
        categoryId: formik?.values?.categoryId,
        vendors: [formik?.values?.name.trim()],
      });
      dispatch(showToast({ message: "Category created", type: "success" }));
      refetchVendors();
      formik.resetForm();
      close(false);
    } catch (e) {}
  };

  const { data: categoriesRes } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const categories = categoriesRes?.data?.map((c) => ({
    label: c.category ?? c.name,
    value: c.id,
  }));

  const formik = useFormik({
    initialValues: { categoryId: null, name: "" },
    validationSchema,
    onSubmit: handleCreateVendor, // <<<
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    if (!open) return;
    formik.setValues(
      {
        categoryId: initialData?.categoryId ?? null,
        name: initialData?.name ?? "",
      },
      false
    );
  }, [open, initialData]);

  return (
    <form>
      <BaseModal
        className="-top-[200px] overflow-auto"
        open={open}
        close={close}
      >
        <div className="py-7 px-12 xl:w-[1000px] lg:w-[900px] sm:w-[700px]">
          <h6 className="text-dark font-normal border-b pb-1">
            {isEdit ? "Edit Category" : "Create New Category"}
          </h6>

          <div className="grid grid-cols-2 gap-x-8 mt-4">
            <BaseAutoSelect
              className="mb-3"
              placeholder="Select technology stack"
              fullHeight={false}
              required
              labelShrink={false}
              label={<span className="font-semibold">Technology Stack Name</span>}
              options={categories}
              id="categoryId"
              name="categoryId"
              isDefaultField={false}
              defaultValue={formik.values.categoryId}
              value={formik.values.categoryId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.categoryId && Boolean(formik.errors.categoryId)
              }
              helperText={formik.touched.categoryId && formik.errors.categoryId}
            />

            <div>
              <p className="font-semibold mb-2">Category Name</p>
              <BaseTextField
                id="name"
                name="name"
                placeholder="Enter category"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>
          </div>
        </div>

        <div
          className="d-flex content-center bg-white py-6 mt-6"
          style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
        >
          <BaseButton
            className="px-[28px] py-[5px] bg-light text-primary !hover:text-primary"
            variant="outlined"
            color="primary"
            style={{ marginRight: "16px" }}
            onClick={() => close(false)}
          >
            Cancel
          </BaseButton>

          <BaseButton
            loaderSize={24}
            loaderColor="primary"
            className="px-[12px] py-[5px]"
            color="primary"
            onClick={formik.handleSubmit}
            disabled={!formik.values.categoryId || !formik.values.name.trim()}
          >
            {isEdit ? "Save" : "Create"}
          </BaseButton>
        </div>
      </BaseModal>
    </form>
  );
}
